import { useEffect, useState } from "react";
import { Col, Container, Row, Button } from "react-bootstrap";
import { injectIntl, defineMessages } from "react-intl";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { far } from '@fortawesome/pro-regular-svg-icons';
import { NavLink } from 'react-router-dom';
import PropTypes from 'prop-types';
import Logo from "./Logo";
import useMessages from "../hooks/useMessages";
import useUserAccount from "../hooks/useUserAccount";
import useEnv from "../hooks/useEnv";
import { toggleLoading } from "../services/helpers";
import SignUpConsentModal from "./SignUpConsentModal";

const messages = defineMessages({
    skipToMainContent: {
        id: "Siirry pääsisältöön",
        defaultMessage: "Siirry pääsisältöön"
    }
});

const Header = (props) => {
    const { isDev } = useEnv();
    const { getUnreadMessageCount } = useMessages();
    const [showModal, setShowModal] = useState(false);
    const lng = props.intl.formatMessage;
    const {
        userIsAuthenticated, userInitIdentityVerification, userIsLoggedIn, userSignOut, userIdentityVerificationUrl,
        userNeedsIdentityVerification, userShouldSwitchToCompany, userInitCompanyAuthentication,
        userChosenTypeIsCompany
    } = useUserAccount();
    const userAuthorized = userIsAuthenticated();

    const handleModalCancel = async () => {
        if (isDev) {
            console.log('User cancelled identity verification');
        }
        await userSignOut();
        setShowModal(false);
        window.location.replace(userIdentityVerificationUrl(userChosenTypeIsCompany));
    }

    const handleModalConfirm = async () => {
        setShowModal(false);
        toggleLoading(true);
        if (isDev) {
            console.log('Redirecting to TUPAS URL for user pool identity verification');
        }
        const authUrl = await userInitIdentityVerification();
        window.location.replace(authUrl);
    }

    /**
     * Redirect to identity verification if user needs it.
     * We should only do this when user is authenticated and not in login screen or something like that.
     * This is the reason we have this effect here.
     */
    useEffect(() => {
        const redirectToAuth = async () => {
            if (userNeedsIdentityVerification) {
                if (isDev) {
                    console.log('User identity verification needed, opening consent modal');
                }
                setShowModal(true);
                props.menuToggle(false);
            } else if (userShouldSwitchToCompany) {
                if (isDev) {
                    console.log('Should switch to company, redirecting to company authentication URL');
                }
                toggleLoading(true);
                // User should act on behalf of a company, redirect to company authentication URL
                const authUrl = await userInitCompanyAuthentication();
                window.location.replace(authUrl);
            } else if (!userIsLoggedIn()) {
                if (isDev) {
                    console.log('User is not logged in, redirecting to landing page');
                }
                // User is not logged in, redirect to landing page
                window.location.replace('/');
            }
        }
        // Check if we are already redirecting to get JWT token
        if (!props.authInProgress) {
            if (isDev) {
                console.log('Auth not in progress, checking if should redirect');
            }
            try {
                void redirectToAuth();
            } catch (error) {
                toggleLoading(false);
            }
        } else if (isDev) {
            console.log('Auth is in progress, do not redirect');
        }
    },[userNeedsIdentityVerification, userShouldSwitchToCompany, props.authInProgress]);

    return (
        <>
            <Button
                className="skip"
                onClick={() => {
                    const main = document.getElementById('main');
                    if (main) {
                        main.setAttribute('tabIndex', '-1'); // make focusable if not already
                        main.focus();
                    }
                }}
            >
                {lng(messages.skipToMainContent)}
            </Button>
            <Container fluid className="g-0" id='header'>
                <Container className="header flex-fill h-100 menubar" >
                    <Row className="mt-2 mt-xl-0 pt-2 pt-xl-0" aria-label="Top">
                        <Col xs={8} className="d-inline-flex justify-content-start align-items-center">
                            <NavLink to={userAuthorized ? "/jobsearch" : "/"}>
                                <Logo height={32} width={119} ariaLabel='MyRopo' />
                            </NavLink>
                        </Col>
                        <Col xs={4} className="d-inline-flex justify-content-end align-items-center">
                            <button aria-label='Menu bars' onClick={props.menuToggle} className="menutoggle" aria-haspopup="true" aria-controls="menu">
                                <FontAwesomeIcon icon={far.faBars} />
                                <div
                                    id="messageCenterUnreadIndicator"
                                    className={`messageCenterUnread icon-need-actions ${getUnreadMessageCount() > 0 ? '' : 'd-none'}`}
                                />
                            </button>
                        </Col>
                    </Row>
                </Container>
            </Container>
            <SignUpConsentModal
                show={showModal}
                onCancel={handleModalCancel}
                onConfirm={handleModalConfirm}
            />
        </>
    )
}

Header.propTypes = {
    menuToggle: PropTypes.func.isRequired,
    intl: PropTypes.object.isRequired,
    authInProgress: PropTypes.bool
};

export default injectIntl(Header);
